import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, createVNode as _createVNode, vModelCheckbox as _vModelCheckbox, withDirectives as _withDirectives, vModelSelect as _vModelSelect, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "container mt-4" }
const _hoisted_2 = { id: "migrationForm" }
const _hoisted_3 = { class: "row" }
const _hoisted_4 = { class: "col-md-6" }
const _hoisted_5 = { class: "col-md-6" }
const _hoisted_6 = { class: "card mb-3" }
const _hoisted_7 = { class: "card-body" }
const _hoisted_8 = { class: "row" }
const _hoisted_9 = { class: "col-md-12" }
const _hoisted_10 = { class: "d-inline-block border-start border-end border-1 p-2" }
const _hoisted_11 = { class: "form-check" }
const _hoisted_12 = {
  class: "d-inline-block border-start border-end border-1 p-2",
  style: {"padding-top":"0 !important","padding-bottom":"0 !important"}
}
const _hoisted_13 = { class: "d-inline-block" }
const _hoisted_14 = { class: "d-inline-block border-start border-end border-1 p-2" }
const _hoisted_15 = { class: "form-check" }
const _hoisted_16 = { class: "d-inline-block border-start border-end border-1 p-2" }
const _hoisted_17 = { class: "form-check" }
const _hoisted_18 = { class: "text-center mb-3" }
const _hoisted_19 = { class: "form-check d-inline-block mb-3" }
const _hoisted_20 = { class: "text-center mb-3" }
const _hoisted_21 = ["disabled"]

import { ref, computed, inject } from 'vue';
    import type * as signalR from '@microsoft/signalr';
    import type * as types from '../types/migration';
    import FeedbackModal from './FeedbackModal.vue';
    import ServerCredentialCard from './ServerCredentialCard.vue';
    import { MigrationRequest } from '../types/migration';
    import type { AuthState, AuthMethods } from '../composables/useAuth';
    import { AUTH_STATE_KEY, AUTH_METHODS_KEY } from '../composables/authKeys';

    interface Props {
        connection: signalR.HubConnection;
    }

    
export default /*@__PURE__*/_defineComponent({
  __name: 'MailApp',
  props: {
    connection: {}
  },
  setup(__props: any) {

    const props = __props;

    const authState = inject(AUTH_STATE_KEY) as AuthState;
    const authMethods = inject(AUTH_METHODS_KEY) as AuthMethods;

    const deleteSource = ref<boolean>(false);
    const foldersOnly = ref<boolean>(false);
    const progressUpdates = ref<boolean>(true);
    const duplicateHandling = ref<string>('Overwrite');

    const tosAccepted = ref<boolean>(false);

    const isStarting = ref<boolean>(false);

    const feedbackModal = ref<InstanceType<typeof FeedbackModal> | null>(null);

    const isSourceAuthenticated = computed(() => {
        const isGeneric = authState.sourceServerType.value === 'generic';
        if (!isGeneric) {
            return !!authState.sourceOAuthToken.value;
        } else {
            return authState.sourceAuthSuccess.value;
        }
    });

    const isDestAuthenticated = computed(() => {
        const isGeneric = authState.destServerType.value === 'generic';
        if (!isGeneric) {
            return !!authState.destOAuthToken.value;
        } else {
            return authState.destAuthSuccess.value;
        }
    });

    const canStartMigration = computed(() => {
        return !isStarting.value && isSourceAuthenticated.value && isDestAuthenticated.value && tosAccepted.value;
    });

    function handleAuthError(title: string, message: string) {
        feedbackModal.value?.alert({
            title,
            message,
            iconClass: 'x-circle'
        });
    }

    function startMigration() {
        if (!tosAccepted.value) {
            feedbackModal.value?.alert({
                title: 'Terms of Service',
                message: 'Please accept the Terms of Service before starting migration',
                iconClass: 'exclamation-triangle'
            });
            return;
        }

        const sourceUseOAuth = authState.sourceServerType.value !== 'generic';
        const destUseOAuth = authState.destServerType.value !== 'generic';

        const request: MigrationRequest = {
            $type: 'Mail',
            destinationCredentials: {
                server: authState.destServer.value,
                user: authState.destUser.value,
                password: destUseOAuth ? "" : authState.destPassword.value,
                oAuthToken: destUseOAuth ? authState.destOAuthToken.value : null,
                useOAuth: destUseOAuth,
                implementer: authState.destServerType.value
            },
            sourceCredentials: {
                server: authState.sourceServer.value,
                user: authState.sourceUser.value,
                password: sourceUseOAuth ? "" : authState.sourcePassword.value,
                oAuthToken: sourceUseOAuth ? authState.sourceOAuthToken.value : null,
                useOAuth: sourceUseOAuth,
                implementer: authState.sourceServerType.value
            },
            options: {
                deleteSource: deleteSource.value,
                foldersOnly: foldersOnly.value,
                duplicateHandling: duplicateHandling.value
            },
            
            progressUpdates: progressUpdates.value,
        };

        if (!request.sourceCredentials?.server) {
            feedbackModal.value?.alert({
                title: 'Validation Error',
                message: 'Please fill in source server',
                iconClass: 'exclamation-triangle'
            });
            return;
        }

        if (!sourceUseOAuth && !request.sourceCredentials?.user) {
            feedbackModal.value?.alert({
                title: 'Validation Error',
                message: 'Please fill in source username',
                iconClass: 'exclamation-triangle'
            });
            return;
        }

        if (!sourceUseOAuth && !request.sourceCredentials?.password) {
            feedbackModal.value?.alert({
                title: 'Validation Error',
                message: 'Please fill in source password',
                iconClass: 'exclamation-triangle'
            });
            return;
        }

        if (sourceUseOAuth && !request.sourceCredentials?.oAuthToken) {
            feedbackModal.value?.alert({
                title: 'Validation Error',
                message: 'Please authenticate with Google for source server',
                iconClass: 'exclamation-triangle'
            });
            return;
        }

        if (!request.destinationCredentials.server) {
            feedbackModal.value?.alert({
                title: 'Validation Error',
                message: 'Please fill in destination server',
                iconClass: 'exclamation-triangle'
            });
            return;
        }

        if (!destUseOAuth && !request.destinationCredentials?.user) {
            feedbackModal.value?.alert({
                title: 'Validation Error',
                message: 'Please fill in destination username',
                iconClass: 'exclamation-triangle'
            });
            return;
        }

        if (!destUseOAuth && !request.destinationCredentials?.password) {
            feedbackModal.value?.alert({
                title: 'Validation Error',
                message: 'Please fill in destination password',
                iconClass: 'exclamation-triangle'
            });
            return;
        }

        if (destUseOAuth && !request.destinationCredentials?.oAuthToken) {
            feedbackModal.value?.alert({
                title: 'Validation Error',
                message: 'Please authenticate with Google for destination server',
                iconClass: 'exclamation-triangle'
            });
            return;
        }

        isStarting.value = true;

        props.connection.invoke("StartMigration", request).catch((err: Error) => {
            console.error(err.toString());
            feedbackModal.value?.alert({
                title: 'Error',
                message: 'Error starting migration: ' + err.toString(),
                iconClass: 'x-circle'
            });
            isStarting.value = false;
        });
    }

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _cache[14] || (_cache[14] = _createElementVNode("h3", { class: "mb-4" }, "Mail Migration Tool", -1 /* CACHED */)),
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("div", _hoisted_4, [
          _createVNode(ServerCredentialCard, {
            direction: "source",
            title: "Source Account",
            headerClass: "bg-primary",
            serverTypeOptions: [{value: 'generic', label: 'IMAP'}, {value: 'Google', label: 'GMail'}],
            migrationType: "Mail",
            useOAuthPredicate: (serverType) => serverType !== 'generic',
            onAuthError: handleAuthError
          }, null, 8 /* PROPS */, ["useOAuthPredicate"])
        ]),
        _createElementVNode("div", _hoisted_5, [
          _createVNode(ServerCredentialCard, {
            direction: "dest",
            title: "Destination Account",
            headerClass: "bg-success",
            serverTypeOptions: [{value: 'generic', label: 'IMAP'}, {value: 'Google', label: 'GMail'}],
            migrationType: "Mail",
            useOAuthPredicate: (serverType) => serverType !== 'generic',
            onAuthError: handleAuthError
          }, null, 8 /* PROPS */, ["useOAuthPredicate"])
        ])
      ]),
      _createElementVNode("div", _hoisted_6, [
        _cache[11] || (_cache[11] = _createElementVNode("div", { class: "card-header" }, [
          _createElementVNode("h5", { class: "mb-0" }, "Migration Options")
        ], -1 /* CACHED */)),
        _createElementVNode("div", _hoisted_7, [
          _createElementVNode("div", _hoisted_8, [
            _createElementVNode("div", _hoisted_9, [
              _createElementVNode("div", _hoisted_10, [
                _createElementVNode("div", _hoisted_11, [
                  _withDirectives(_createElementVNode("input", {
                    class: "form-check-input",
                    type: "checkbox",
                    id: "progressUpdates",
                    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((progressUpdates).value = $event))
                  }, null, 512 /* NEED_PATCH */), [
                    [_vModelCheckbox, progressUpdates.value]
                  ]),
                  _cache[5] || (_cache[5] = _createElementVNode("label", {
                    class: "form-check-label",
                    for: "progressUpdates"
                  }, " Progress Updates ", -1 /* CACHED */))
                ])
              ]),
              _createElementVNode("div", _hoisted_12, [
                _cache[7] || (_cache[7] = _createElementVNode("div", { class: "d-inline-block" }, [
                  _createElementVNode("label", {
                    for: "duplicateHandling",
                    class: "form-label"
                  }, "Duplicate Handling")
                ], -1 /* CACHED */)),
                _cache[8] || (_cache[8] = _createElementVNode("div", { class: "d-inline-block" }, "  ", -1 /* CACHED */)),
                _createElementVNode("div", _hoisted_13, [
                  _withDirectives(_createElementVNode("select", {
                    class: "form-select",
                    id: "duplicateHandling",
                    "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((duplicateHandling).value = $event))
                  }, [...(_cache[6] || (_cache[6] = [
                    _createElementVNode("option", { value: "Overwrite" }, "Overwrite", -1 /* CACHED */),
                    _createElementVNode("option", { value: "Skip" }, "Skip", -1 /* CACHED */),
                    _createElementVNode("option", { value: "CreateDuplicate" }, "Create Duplicate", -1 /* CACHED */)
                  ]))], 512 /* NEED_PATCH */), [
                    [_vModelSelect, duplicateHandling.value]
                  ])
                ])
              ]),
              _createElementVNode("div", _hoisted_14, [
                _createElementVNode("div", _hoisted_15, [
                  _withDirectives(_createElementVNode("input", {
                    class: "form-check-input",
                    type: "checkbox",
                    id: "deleteSource",
                    "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((deleteSource).value = $event))
                  }, null, 512 /* NEED_PATCH */), [
                    [_vModelCheckbox, deleteSource.value]
                  ]),
                  _cache[9] || (_cache[9] = _createElementVNode("label", {
                    class: "form-check-label",
                    for: "deleteSource"
                  }, " Delete Source ", -1 /* CACHED */))
                ])
              ]),
              _createElementVNode("div", _hoisted_16, [
                _createElementVNode("div", _hoisted_17, [
                  _withDirectives(_createElementVNode("input", {
                    class: "form-check-input",
                    type: "checkbox",
                    id: "foldersOnly",
                    "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((foldersOnly).value = $event))
                  }, null, 512 /* NEED_PATCH */), [
                    [_vModelCheckbox, foldersOnly.value]
                  ]),
                  _cache[10] || (_cache[10] = _createElementVNode("label", {
                    class: "form-check-label",
                    for: "foldersOnly"
                  }, " Folders Only ", -1 /* CACHED */))
                ])
              ])
            ])
          ])
        ])
      ]),
      _createElementVNode("div", _hoisted_18, [
        _createElementVNode("div", _hoisted_19, [
          _withDirectives(_createElementVNode("input", {
            class: "form-check-input",
            type: "checkbox",
            id: "tosCheckbox",
            "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((tosAccepted).value = $event))
          }, null, 512 /* NEED_PATCH */), [
            [_vModelCheckbox, tosAccepted.value]
          ]),
          _cache[12] || (_cache[12] = _createElementVNode("label", {
            class: "form-check-label",
            for: "tosCheckbox"
          }, [
            _createTextVNode(" I agree to the "),
            _createElementVNode("a", {
              href: "/Tos",
              target: "_blank"
            }, "Terms of Service")
          ], -1 /* CACHED */))
        ])
      ]),
      _createElementVNode("div", _hoisted_20, [
        _createElementVNode("button", {
          id: "startBtn",
          class: "btn btn-primary btn-lg",
          onClick: startMigration,
          disabled: !canStartMigration.value
        }, [...(_cache[13] || (_cache[13] = [
          _createElementVNode("i", { class: "bi bi-play-fill" }, null, -1 /* CACHED */),
          _createTextVNode(" Start Migration ", -1 /* CACHED */)
        ]))], 8 /* PROPS */, _hoisted_21)
      ])
    ]),
    _createVNode(FeedbackModal, {
      ref_key: "feedbackModal",
      ref: feedbackModal
    }, null, 512 /* NEED_PATCH */)
  ]))
}
}

})